// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// WB-007 — a transparent drawing canvas anchored exactly over a shared-screen <video>,
// so marks land on the SAME content pixel for every viewer regardless of window size.
// The video is `object-fit: contain` (letterboxed), so the canvas is sized/positioned to
// the picture's content sub-rectangle (videoContentRect) and coordinates are normalized
// to the video's INTRINSIC resolution — that is the WhiteboardEngine's referenceSize.
//
// Live ops flow over the call's transport (WB-003 data channel in 1:1): local strokes are
// stamped with this board's id and sent; incoming ops for this board are applied. The
// overlay clears when the share stops (the parent simply unmounts it).

import { useEffect, useRef, useState } from "react";
import { WhiteboardEngine } from "../whiteboard/engine";
import { videoContentRect } from "../whiteboard/anchor";
import { ANNOTATION_PALETTE } from "../whiteboard/colors";
import type { WireOp } from "../whiteboard/types";

// How long a screen annotation stays before it fades out (Slack-style transience).
const ANNOTATION_FADE_MS = 6000;

// AnnotationConfig wires an overlay to a call transport (shared by CallView/GroupCallView).
export interface AnnotationConfig {
  author: string;
  boardId: string;
  color: string;
  enabled: boolean; // may THIS client draw (presenter gate, WB-008)
  sendOp: (op: WireOp) => void;
  register: (handler: (op: WireOp) => void) => () => void;
}

export function AnnotationOverlay({
  videoRef,
  author,
  boardId,
  color,
  enabled,
  sendOp,
  register,
}: {
  readonly videoRef: React.RefObject<HTMLVideoElement | null>;
  readonly author: string;
  readonly boardId: string;
  readonly color: string;
  readonly enabled: boolean; // may THIS client draw (presenter gate, WB-008)
  readonly sendOp: (op: WireOp) => void;
  readonly register: (handler: (op: WireOp) => void) => () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<WhiteboardEngine | null>(null);
  // Current pen color — defaults to the author's deterministic color, overridable via the
  // toolbar swatches. The chosen color rides on each stroke op, so peers render it directly.
  const [pen, setPen] = useState(color);
  // Keep the latest sendOp/boardId in refs so the engine's onLocalOp closure (created
  // once) always uses current values without recreating the engine.
  const sendRef = useRef(sendOp);
  sendRef.current = sendOp;
  const boardRef = useRef(boardId);
  boardRef.current = boardId;

  // Create the engine once the canvas is mounted; wire local→transport and the resize
  // anchoring. Torn down fully on unmount (WB-021).
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const intrinsic = () => {
      const v = videoRef.current;
      return { width: v?.videoWidth ?? 0, height: v?.videoHeight ?? 0 };
    };

    // Position + size the canvas to the video's letterboxed content box.
    const layout = () => {
      const intr = intrinsic();
      const rect = videoContentRect(intr, { width: container.clientWidth, height: container.clientHeight });
      const dpr = globalThis.devicePixelRatio || 1;
      canvas.style.left = `${rect.left}px`;
      canvas.style.top = `${rect.top}px`;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const bw = Math.max(1, Math.round(rect.width * dpr));
      const bh = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw; // changing the backing size clears the canvas…
        canvas.height = bh;
        engineRef.current?.refresh(); // …so repaint the op-log
      }
    };

    const engine = new WhiteboardEngine(canvas, {
      author,
      color,
      referenceSize: { width: intrinsic().width || 1920, height: intrinsic().height || 1080 },
      // Screen annotations are transient (Slack-style): marks fade and clear themselves so
      // they don't clutter the presenter's screen. The Mode B board does NOT set this.
      fadeMs: ANNOTATION_FADE_MS,
      onLocalOp: (op) => sendRef.current({ ...op, board_id: boardRef.current }),
    });
    engineRef.current = engine;

    // Apply inbound ops addressed to THIS board (the call transport multiplexes boards).
    const unregister = register((op) => {
      if (op.board_id && op.board_id !== boardRef.current) return;
      if (op.op === "stroke" || op.op === "erase" || op.op === "clear") engine.applyRemote(op);
    });

    layout();
    const ro = new ResizeObserver(layout);
    ro.observe(container);
    const v = videoRef.current;
    v?.addEventListener("loadedmetadata", layout);
    globalThis.addEventListener("resize", layout);

    return () => {
      unregister();
      ro.disconnect();
      v?.removeEventListener("loadedmetadata", layout);
      globalThis.removeEventListener("resize", layout);
      engine.destroy();
      engineRef.current = null;
    };
    // Engine identity is intentionally stable for the overlay's lifetime; author is fixed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow the author default if it changes, then reflect the active pen to the engine.
  useEffect(() => setPen(color), [color]);
  useEffect(() => {
    engineRef.current?.setColor(pen);
  }, [pen]);

  // "Undo my last mark" / "clear my marks" act only on ops authored by ME (the engine
  // tracks author per op) — never touching others' annotations. undo() emits an erase so
  // peers converge.
  const undoMine = () => {
    const e = engineRef.current;
    if (!e) return;
    const mine = e.ops.filter((o) => o.author === author);
    const last = mine[mine.length - 1];
    if (last) e.undo(last.id);
  };
  const clearMine = () => {
    const e = engineRef.current;
    if (!e) return;
    for (const o of e.ops) if (o.author === author) e.undo(o.id);
  };

  return (
    <div ref={containerRef} className="wb-overlay">
      <canvas
        ref={canvasRef}
        className="wb-overlay-canvas"
        style={{ pointerEvents: enabled ? "auto" : "none", touchAction: "none" }}
      />
      {enabled && (
        <div className="wb-toolbar">
          {ANNOTATION_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              className={`wb-swatch${c === pen ? " wb-swatch-active" : ""}`}
              style={{ background: c }}
              onClick={() => setPen(c)}
              title="Pen color"
              aria-label={`Pen color ${c}`}
            />
          ))}
          <button type="button" className="wb-tool" onClick={undoMine} title="Undo my last mark">
            ↶
          </button>
          <button type="button" className="wb-tool" onClick={clearMine} title="Clear my marks">
            ⌫
          </button>
        </div>
      )}
    </div>
  );
}
